/**
 * Playlist Track Utils
 *
 * @module lib/PlaylistTrackUtils
 */
import { fromJS, List } from 'immutable';
import isString from 'lodash/isString';
import memoize from 'lodash/memoize';

import { LyricsCountries } from '../constants/CountryCodes';
import { TrackType } from '../constants/TrackType';
import { getInDomain } from '../domains/externalUtils';
import { getCountryCode } from '../lib/configuration/TunerVarUtils';
import { type ValueOf } from '../types';

import { getTrackById, getTrackEntitiesFromState, getAnnotationsById } from './EntityUtils';

export { getTrackById };
export { getTrackEntitiesFromState };

/**
 * returns true if track is thumbed up.
 *
 * @param  {Map} track - track
 * @returns {boolean} - track thumbed up?
 */
export function isTrackThumbedUp(track: $TSFixMe): boolean {
  return track.get('rating') > 0;
}

/**
 * returns true if track is thumbed down.
 *
 * @param  {Map} track - track
 * @returns {boolean} - track thumbed down?
 */
export function isTrackThumbedDown(track: $TSFixMe): boolean {
  return track.get('rating') < 0;
}

export function getThumbedUpDate(track: $TSFixMe): string {
  return (
    track.getIn(['tprFirstThumbed', 'timestamp']) || track.getIn(['tprLastThumbed', 'timestamp'])
  );
}

export function getThumbprintStationId(track: $TSFixMe): string {
  return (
    track.getIn(['tprFirstThumbed', 'stationId']) || track.getIn(['tprLastThumbed', 'stationId'])
  );
}

export function getLyricSnippet(track: $TSFixMe, state: $TSFixMe): $TSFixMe | null {
  const lyricSnippet = track.get('lyricSnippet');
  const isLyricsCountry = LyricsCountries.has(getCountryCode(state));
  return lyricSnippet && isLyricsCountry ? lyricSnippet : null;
}

export function getLyricSnippetAPS(state: $TSFixMe): $TSFixMe | null {
  const lyricSnippet = getInDomain('playback.selectors.getCurrentItemLyricSnippet', '')(state);
  const isLyricsCountry = LyricsCountries.has(getCountryCode(state));

  return lyricSnippet && isLyricsCountry ? lyricSnippet : null;
}

export function getTrackType(track: $TSFixMe): ValueOf<typeof TrackType> {
  // tracks served from getFragment have a 'trackType' key, but those served from APS have a nested 'sessionItem' with a 'type'
  return track ? track.get('trackType', track.getIn(['sessionItem', 'type'], null)) : null;
}

export function isAudioWarning(track: $TSFixMe): boolean {
  return getTrackType(track) === TrackType.AUDIO_WARNING;
}

export function isArtistMessage(track: $TSFixMe): boolean {
  return getTrackType(track) === TrackType.ARTIST_MESSAGE;
}

export function getArtistMessageDeliveryType(track: $TSFixMe): $TSFixMe {
  return track && isArtistMessage(track) && track.getIn(['artistMessage', 'deliveryType'], null);
}

export function isRegularTrack(track: $TSFixMe): boolean {
  return getTrackType(track) === TrackType.TRACK;
}

export function isCustomTrack(track: $TSFixMe): boolean {
  return getTrackType(track) === TrackType.CUSTOM_TRACK;
}

export function isRegularOrCustomTrack(track: $TSFixMe): boolean {
  return isRegularTrack(track) || isCustomTrack(track);
}

export function getArtistMessageId(track: $TSFixMe): string {
  return track.getIn(['artistMessage', 'artistMessageId']);
}

export function getArtistUid(track: $TSFixMe): string {
  return track.getIn(['artistMessage', 'artistUid']);
}

export function getArtistMessageIdAPS(pandoraId: string): string {
  return pandoraId.toString().split(':').pop() || '';
}

export function getArtistUidAPS(state: $TSFixMe, pandoraId: string): string {
  const trackAnnotation = getAnnotationsById(state, pandoraId);
  const authorId = trackAnnotation?.get('authorId');
  return authorId ? `R${authorId.toString().split(':').pop()}` : '';
}

export function getArtistMessageButtonURL(track: $TSFixMe): string {
  return track.getIn(['artistMessage', 'buttonURL']);
}

export function getArtistMessageButtonText(track: $TSFixMe): string {
  return track.getIn(['artistMessage', 'buttonText']);
}

export function getTrackArtist(track: $TSFixMe): string {
  return track ? track.get('artistName') : '';
}

export function getTrackRights(track: $TSFixMe, field: $TSFixMe): boolean {
  const rights = (track && track.get('rights')) || [];
  return rights.indexOf(field) > -1;
}

export function getTrackTemplatePath(track: $TSFixMe) {
  return track ? track.get('templatePath') : null;
}

export function getTrackAudioToken(track: $TSFixMe) {
  return track ? track.get('audioTokenId') : null;
}

export function getTrackMusicId(track: $TSFixMe) {
  return track ? track.get('musicId') : null;
}

export function getTrackAudioEncoding(track: $TSFixMe) {
  return track ? track.get('audioEncoding') : null;
}

export function getTrackStationId(track: $TSFixMe) {
  return track ? track.get('stationId') : null;
}

export function isTrackReplayableWithoutReward(track: $TSFixMe): boolean {
  return !getTrackRights(track, 'replayRequiresReward');
}

export function doesTrackAllowSkipAfterLimit(track: $TSFixMe): boolean {
  return getTrackRights(track, 'allowSkipAfterLimit');
}

export function getRightsShowReplayButton(track: $TSFixMe): boolean {
  return getTrackRights(track, 'showReplayButton');
}

export function doesTrackHaveReplayRights(track: $TSFixMe): boolean {
  return getTrackRights(track, 'allowReplay');
}

export function doesTrackAllowLimitlessSkips(track: $TSFixMe): boolean {
  return track && track.get('allowSkipTrackWithoutLimit');
}

// We want getEnabledPlaybackControls to return identical immutable objects when
// the set of enabled controls is the same, this will prevent unnecessary rerenders
// that would otherwise trigger because `List() !== List()`
// (see https://medium.com/@esamatti/react-js-pure-render-performance-anti-pattern-fb88c101332f#.lsj5y44w3)
export const controlsMemoizationHelper = memoize(object => {
  let returnVal = object;
  if (isString(object)) {
    returnVal = object.split(',');
  }
  return fromJS(returnVal) || List();
});

const SESSION_HISTORY_TYPES = List([
  TrackType.TRACK,
  TrackType.ARTIST_MESSAGE,
  TrackType.VOICE_TRACK,
  TrackType.CUSTOM_TRACK,
] as const);

// Only certain tracks should persist in session history
// Others only get played once and aren't stored
export function shouldTrackPersistInSessionHistory(track: $TSFixMe): boolean {
  if (!track) {
    return false;
  }
  return SESSION_HISTORY_TYPES.includes(getTrackType(track));
}

export function shouldTypePersistInSessionHistory(type: $TSFixMe): boolean {
  if (!type) return false;
  return SESSION_HISTORY_TYPES.includes(type);
}

export function getArtistAudioMessagesInCurrentPlaylist(state: $TSFixMe) {
  const id = getInDomain('playback.utils.getCurrentPlaylist')(state);
  const playlistTrackTokens = getInDomain('playback.utils.getPlaylistTrackIds')(state, id);
  return playlistTrackTokens
    .filter((playlistTrackToken: $TSFixMe) => {
      const playlistTrack = getTrackById(state, playlistTrackToken);
      return playlistTrack && isArtistMessage(playlistTrack);
    })
    .toSet();
}
